import { addDays, addMinutes, addMonths, differenceInCalendarWeeks, differenceInMinutes, format, getDay, isValid, parseISO } from 'date-fns';

// Regras da série de agendamentos. Trabalha só com textos 'yyyy-MM-dd' e 'HH:mm':
// new Date('yyyy-MM-dd') e toISOString trocam o dia em UTC-3.

export const SERIES_MAX_SESSIONS = 52;
export const SERIES_MIN_SESSIONS = 2;
export const SERIES_DEFAULT_SESSIONS = 4;
export const SERIES_HORIZON_MONTHS = 12;

export const WEEK_DAYS = [
  { value: 1, short: 'Seg', name: 'Segunda-feira' },
  { value: 2, short: 'Ter', name: 'Terça-feira' },
  { value: 3, short: 'Qua', name: 'Quarta-feira' },
  { value: 4, short: 'Qui', name: 'Quinta-feira' },
  { value: 5, short: 'Sex', name: 'Sexta-feira' },
  { value: 6, short: 'Sáb', name: 'Sábado' },
];

const SHORT_DAY_NAMES = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
const MONTH_NAMES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

export const isValidDate = (text) => /^\d{4}-\d{2}-\d{2}$/.test(text ?? '') && isValid(parseISO(text));
export const isValidTime = (text) => /^([01]\d|2[0-3]):[0-5]\d$/.test(text ?? '');
export const formatDateBR = (text) => (isValidDate(text) ? format(parseISO(text), 'dd/MM/yyyy') : '');
export const shortWeekDay = (text) => (isValidDate(text) ? SHORT_DAY_NAMES[getDay(parseISO(text))] : '');
export const todayText = () => format(new Date(), 'yyyy-MM-dd');

const horizonLimit = (firstDate) => format(addMonths(parseISO(firstDate), SERIES_HORIZON_MONTHS), 'yyyy-MM-dd');
const sessionOrder = (a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`);

// Datas geradas pela regra. A 1ª é sempre firstDate; as semanas vão de segunda a domingo.
// Datas puladas são repostas no fim e a geração para em 12 meses.
// skippedInRule: as puladas que a regra atual ainda gera (as outras não aparecem na tela)
export function generateDates({ firstDate, weekDays = [], intervalWeeks = 1, count, skipped = [] }) {
  if (!isValidDate(firstDate)) return { dates: [], skippedInRule: [], requested: 0 };

  const start = parseISO(firstDate);
  const every = Math.max(Number(intervalWeeks) || 1, 1);
  const requested = Math.min(Math.max(Number(count) || 1, 1), SERIES_MAX_SESSIONS);
  const limit = addMonths(start, SERIES_HORIZON_MONTHS);
  const dates = [firstDate];
  const skippedInRule = [];

  for (let day = addDays(start, 1); weekDays.length > 0 && dates.length < requested && day <= limit; day = addDays(day, 1)) {
    const week = differenceInCalendarWeeks(day, start, { weekStartsOn: 1 });
    if (week % every !== 0 || !weekDays.includes(getDay(day))) continue;

    const date = format(day, 'yyyy-MM-dd');
    if (skipped.includes(date)) skippedInRule.push(date);
    else dates.push(date);
  }

  return { dates, skippedInRule, requested };
}

// Sessões na ordem da tela. A 1ª espelha os campos de cima; as outras podem ter data e horário
// próprios (ajustes guardados pela chave da linha). Datas avulsas entram com o horário de cima
export function buildSessions({ dates, extraDates = [], startTime, endTime, adjustments = {} }) {
  if (dates.length === 0) return [];

  const [first, ...others] = dates;
  const row = (key, date, extra) => {
    const adjustment = adjustments[key] ?? {};
    const adjustedDate = adjustment.date ?? date;

    return {
      key,
      extra,
      date: adjustedDate,
      start_time: adjustment.start_time ?? startTime,
      end_time: adjustment.end_time ?? endTime,
      ownTime: 'start_time' in adjustment,
      // Data incompleta durante a digitação: a linha fica no lugar da data original
      order: isValidDate(adjustedDate) ? adjustedDate : date,
    };
  };

  const rest = [
    ...others.map((date) => row(date, date, false)),
    ...extraDates.map((date) => row(`extra:${date}`, date, true)),
  ].sort((a, b) => `${a.order}${a.start_time}`.localeCompare(`${b.order}${b.start_time}`));

  return [{ key: first, main: true, extra: false, date: first, start_time: startTime, end_time: endTime, ownTime: false, order: first }, ...rest];
}

// Novo fim mantendo a duração; null se passar da meia-noite ou se algum horário for inválido
export function moveStart(session, newStart) {
  if (![newStart, session.start_time, session.end_time].every(isValidTime)) return null;

  const time = (text) => parseISO(`2000-01-01T${text}`);
  const end = addMinutes(time(newStart), differenceInMinutes(time(session.end_time), time(session.start_time)));

  return format(end, 'yyyy-MM-dd') === '2000-01-01' ? format(end, 'HH:mm') : null;
}

// Marcas calculadas na tela, sem servidor. Mesma regra de bordas e de prazo do backend
export function validateSessions(sessions, today = todayText()) {
  const marks = {};
  const limit = sessions.length > 0 && isValidDate(sessions[0].date) ? horizonLimit(sessions[0].date) : null;

  sessions.forEach((session, index) => {
    const repeated = sessions.findIndex((other, j) => j !== index && other.date === session.date && other.start_time < session.end_time && other.end_time > session.start_time);

    if (!isValidDate(session.date))
      marks[session.key] = { type: 'error', label: 'Data inválida' };
    else if (!isValidTime(session.start_time) || session.start_time === '00:00')
      marks[session.key] = { type: 'error', label: 'Horário inválido', detail: session.start_time === '00:00' ? 'O início não pode ser 00:00.' : 'Informe o início.' };
    else if (!isValidTime(session.end_time) || session.end_time <= session.start_time)
      marks[session.key] = { type: 'error', label: 'Horário inválido', detail: 'O fim precisa ser depois do início, no mesmo dia.' };
    else if (repeated >= 0)
      marks[session.key] = { type: 'error', label: 'Repetida', detail: `Mesmo horário da sessão nº ${repeated + 1}.` };
    else if (limit && session.date > limit)
      marks[session.key] = { type: 'error', label: 'Fora do prazo', detail: 'Passa de 12 meses depois da 1ª sessão.' };
    else if (session.date < today)
      marks[session.key] = { type: 'warning', label: 'Data passada' };
    else if (getDay(parseISO(session.date)) === 0)
      marks[session.key] = { type: 'warning', label: 'Domingo' };
    else if (!session.main && session.date < sessions[0].date)
      marks[session.key] = { type: 'warning', label: 'Antes da 1ª sessão' };
  });

  return marks;
}

// "Salvar e próximo" e "Salvar e agendar próximas": uma semana depois da sessão de origem,
// no mesmo dia da semana, mas nunca antes de hoje
export function nextWeeklyDate(originDate, today = todayText()) {
  if (!isValidDate(originDate)) return '';

  let next = addDays(parseISO(originDate), 7);
  while (format(next, 'yyyy-MM-dd') < today) next = addDays(next, 7);

  return format(next, 'yyyy-MM-dd');
}

// Corpo de cada sessão para a API: horário de parede, sem fuso
export const buildOccurrences = (sessions) => sessions.map((session) => ({
  data_agendamento_inicial: `${session.date}T${session.start_time}`,
  data_agendamento_final: `${session.date}T${session.end_time}`,
}));

// Resumo do lote salvo: datas agrupadas por mês, com o horário quando for diferente do de cima
export function groupSessionsByMonth(sessions, mainStartTime) {
  const groups = [];

  [...sessions].filter((session) => isValidDate(session.date)).sort(sessionOrder).forEach((session) => {
    const date = parseISO(session.date);
    const title = `${MONTH_NAMES[date.getMonth()]}/${date.getFullYear()}`;
    let group = groups[groups.length - 1];

    if (!group || group.title !== title) {
      group = { title, items: [] };
      groups.push(group);
    }

    group.items.push(`${shortWeekDay(session.date)} ${format(date, 'dd')}${session.start_time !== mainStartTime ? ` (${session.start_time})` : ''}`);
  });

  return groups;
}
